import { createSelector } from 'reselect';
import { getSeriesOption } from '../views/series/selectors/eventSeries';
import { hasSelectedChildEvent } from '../views/series/selectors/eventSeries';
import { TICKET_FEE_SEPARATE, TICKET_PRICE_ONLY } from '../reducers/ui';

const getSteps = state => state.ui.listingConfig ? state.ui.listingConfig.checkoutSteps: [];
const getCurrentStep = state => state.ui.currentStep;
export const getCaptureMethod = state => state.payment.method;
const getPurchaseUser = state => state.reservation.purchaseUser;
const getTerminalId = state => state.payment.terminalId;
const getTerminalConnection = state => state.payment.isReaderConnected;
const seriesConfig = state => state.ui.checkoutConfig.seriesOptions;
export const getListingConfig = state => state.ui && state.ui.listingConfig ? state.ui.listingConfig : {};
const getUiConfig = state => state.ui.uiConfig;
const getCheckoutConfig = state => state.ui && state.ui.checkoutConfig ? state.ui.checkoutConfig : {};
const isCalendarOnListing = state => getSeriesOption("calendarOnListing", false)(state);
const getRouter = state => state.router;
const getAccessInventory = state => state.coupon.accessInventory;
const getEvent = state => state.event;

export const allStepsComplete = createSelector(
    [getSteps, getListingConfig],
    (steps, config) => {
        let complete = true;
        for(let i = 0; i < config.checkoutSteps.length; i++) {
            if(config.checkoutSteps[i].complete !== true) {
                complete = false;
                break;
            }
        }
        return complete;
    }
)

export const getCurrentCheckoutStep = createSelector(
    [getSteps, getCurrentStep],
    (steps, currentStepIndex) => {
        return Array.isArray(steps) && steps.length > currentStepIndex ? steps[currentStepIndex] : null;
    }
)

export const inLastStep = createSelector(
    [getSteps, getCurrentStep],
    (steps, currentStepIndex) => {
        return Array.isArray(steps) && currentStepIndex === steps.length - 1;
    }
)

export const getBlockingErrors = createSelector(
    [
        getCaptureMethod,
        getPurchaseUser,
        getTerminalId,
        getTerminalConnection,
        getListingConfig
    ],
    (
        method,
        purchaseUser,
        terminalId,
        terminalConnected,
        config
    ) => {
        let errors = [];
        // corresponds to CAPTURE_KIOSK
        if(method === 13) {
            let cmsMode = getCmsModeFromConfig(config);
            if(!cmsMode) {
                if (!purchaseUser || purchaseUser == 0) {
                    errors.push("A kiosk user must be set");
                }

                if(!isDevMode()) {
                    if (+terminalId === 0) {
                        errors.push("No EMV terminal selected");
                    } else if (!terminalConnected) {
                        errors.push("EMV terminal is disconnected");
                    }
                }
            }
        }

        return errors;
    }
);

export const isKioskMode = createSelector(
    [
        getCaptureMethod,
    ],
    (
        captureMethod
    ) => {
        // corresponds to CAPTURE_KIOSK
        if(captureMethod === 13) {
            return true;
        }

        return false;
    }
);

export const getSeriesConfig = createSelector(
    [
        seriesConfig,
    ],
    (
        config
    ) => {
        if(config) {
            return config;
        }

        return {};
    }
);

export const getListingSettings = createSelector(
    [
        getListingConfig,
        isCalendarOnListing,
    ],
    (
        config,
        calendarOnListing
    ) => {
        let settings = {};
        if(config && config.listing) {
            settings = config.listing;
        } else if (calendarOnListing) {
            settings = getListingSettingsWithCalendar();
        } else {
            settings = getDefaultListingSettings(config);
        }

        return settings;
    }
);

function getDefaultListingSettings(config) {
    if(config.layout === "kiosk") {
        return {
            components: [
                {
                    name: 'continueCheckoutBanner',
                },
                {
                    name: 'KioskListing',
                    children: [
                        {
                            name: "SoldOutDayDisplay",
                        },
                        {
                        name: 'ticketsButton',
                            props: {type: 'banner__touch'},
                            children: [
                                {
                                    name: "NextAvailableTime",
                                },
                            ]
                        },
                        {
                            name: "TicketsGoOnSale",
                        }
                    ]
                },
                {
                    name: "KioskManagement",
                },
                {
                    name: "KioskMonitoring",
                    props: {
                        // 'interval' => 1000 // in ms
                    }
                },
                {
                    name: "TimeClock",
                },
                {
                    name: "RefreshListingData",
                },
                {
                    name: "KioskTimeContainer",
                },
                {
                    name: "TimeoutPromptContainer",
                },
            ]
        }
    } else {
        return {
            components: [
                {
                    name: 'continueCheckoutBanner',
                },
                {
                    name: 'listingBanner',
                    children: [
                        {
                            name: 'ticketsButton',
                        }
                    ]
                },
                {
                    name: 'listingDetails',
                },
                {
                    name: 'contactInfo',
                },
                {
                    name: 'footer',
                },
            ],
        }
    }
}

function getListingSettingsWithCalendar() {
    return {
        components: [
            {
                name: 'continueCheckoutBanner',
            },
            {
                name: 'listingBanner',
                children: [
                    {
                        name: 'listingDatePicker',
                    }
                ]
            },
            {
                name: 'listingDetails',
            },
            {
                name: 'contactInfo',
            },
            {
                name: 'footer',
            },
        ],
    };
}

export const getCheckoutStepsSettings = createSelector(
    [
        getListingConfig,
        getCurrentStep,
    ],
    (
        config,
        currentStep
    ) => {
        let settings = {};
        if(config && config.checkoutSteps) {
            settings = config.checkoutSteps[currentStep];
        } else {
            const steps = getDefaultCheckoutStepSettings();
            settings = steps[currentStep];
        }

        return settings;
    }
);

export function getDefaultCheckoutStepSettings(config, hasQuestions, hasDeliveryOptions, isFree) {
    if(config.layout === "kiosk") {
        let checkoutSteps = [];

        const kioskOptions = getKioskOptionsFromConfig(config);
        if (kioskOptions.collectCustomerName || kioskOptions.collectCustomerEmail || hasQuestions || hasDeliveryOptions) {
            checkoutSteps.push({
                title: 'Registration',
                components: [
                    {
                        name: 'memberInfo',
                    },
                    {
                        name: 'delivery',
                    },
                    {
                        name: 'questions',
                    },
                ],
            });
        }
        if (!isFree) {
            let step = {
                title: "Payment",
                components: [
                    {
                        'name': "TimeoutPromptContainer"
                    },
                ],
            };

            if(isDevMode()) {
                step.components.push({
                    'name': "payment"
                });
            } else {
                step.components.push({
                    'name': "StripeEmvPayment"
                });
            }
            checkoutSteps.push(step);
        }
        return checkoutSteps;
    } else {
        return [
            {
                title: 'Registration',
                components: [
                    {
                        name: 'memberInfo',
                    },
                    {
                        name: 'questions',
                    },
                    {
                        name: 'delivery',
                    },
                    {
                        name: 'insurance',
                    },
                    {
                        name: 'donation',
                    },
                ],
            },
            {
                title: 'Payment',
                components: [
                    {
                        name: 'payment',
                    },
                ],
            }
        ];
    }
}

export const getTicketPickerSettings = createSelector(
    [
        getListingConfig,
        getEvent
    ],
    (
        config,
        event,
    ) => {
        //if we ever make it to this state, render an empty, but recoverable ticketpicker
        if (Object.values(event.events)[0].event.isRecurring && !hasSelectedChildEvent({event})) {
            return {
                components: [
                    {
                        name: "TicketPickerContainer",
                        children: [
                            {
                                name: 'InventoryHeaderContainer',
                            },
                            {
                                name: 'InventoryFooterContainer',
                            }
                        ],
                    },
                ],
            };
        }
        let settings = {};
        if(config && config.ticketPicker) {
            settings = config.ticketPicker;
        } else {
            settings = getDefaultTicketPickerSettings(config);
        }

        return settings;
    }
);

function getDefaultTicketPickerSettings(config) {
    if (config.layout === 'kiosk') {
        return {
            components: [
                {
                    name:"KioskLayoutContainer",
                    props:{
                        splitPane: true,
                        couponAlerts: true,
                        inventoryErrors: true,
                    },
                    children: [
                        {
                            name: 'InventoryContentContainer',
                            children: [
                                {
                                    name: 'InventoryItemsContainer',
                                    children: [
                                        {
                                            name: 'PriceLevelWrapper',
                                            children: [
                                                {
                                                    name: 'ChangeDateContainer',
                                                }
                                            ]
                                        },
                                    ]
                                },
                            ]
                        },
                    ],
                }
            ],
        };
    }

    return {
        components: [
            {
                name: "TicketPickerContainer",
                children: [
                    {
                        name: 'CouponAlertsContainer',
                    },
                    {
                        name: 'InventoryHeaderContainer',
                    },
                    {
                        name: 'AlertContainer',
                    },
                    {
                        name: 'InventoryContentContainer',
                        children: [
                            {
                                name: 'ChangeDateContainer'
                            },
                            {
                                name: 'InventoryItemsContainer',
                            },
                        ]
                    },
                    {
                        name: 'InventoryFooterContainer',
                        children: [
                            {
                                name: 'InventoryErrors',
                            },
                            {
                                name: 'FeeDisclosure',
                            },
                            {
                                name: 'CheckoutButton',
                            },
                            {
                                name: "CouponEntryButton",
                            },
                        ],
                    },
                ],
            },
        ],
    };
}

export const getCustomInventory = createSelector(
    [
        getCheckoutConfig,
        getAccessInventory,
    ],
    (
        checkoutConfig,
        accessInventory
    ) => {
        if (accessInventory) {
            return accessInventory;
        }
        return checkoutConfig.inventory ? checkoutConfig.inventory : null;
    }
);

export const getConfirmationPageSettings = createSelector(
    [
        getListingConfig,
    ],
    (
        config,
    ) => {
        let settings = {};
        if(config && config.confirmation) {
            settings = config.confirmation;
        } else {
            return getDefaultConfirmationSettings(config);
        }

        return settings;
    }
);

function getDefaultConfirmationSettings(config) {
    if(config.layout === "kiosk") {
        return {
            components: [
                {
                    'name': "ConfirmationHeader",
                },
                {
                    'name': "KioskConfirmationContainer",
                },
                {
                    'name': "KioskGoBackButton",
                },
                {
                    'name': "GoBackTimeout",
                },
                {
                    'name': "SidekickTicketPrint",
                },
            ],
        };
    } else {
        return {
            components: [
                {
                    name: 'ConfirmationContainer',
                },
            ]
        };
    }
}

export const getConfirmationConfig = createSelector([getUiConfig], (uiConfig) => {
    let config = {};

    if(uiConfig && uiConfig.checkout && uiConfig.checkout.confirmation) {
        config = uiConfig.checkout.confirmation;
    }

    return config;
});

export function getKioskOptionsFromConfig(config) {
    return config.checkout && config.checkout.kioskOptions ? config.checkout.kioskOptions : {};
}

export const getKioskOptions = createSelector(
    [
        getListingConfig,
    ],
    (
        config,
    ) => {
        return getKioskOptionsFromConfig(config);
    }
);

function getCmsModeFromConfig(config) {
    return config.checkout && config.checkout.cmsMode ? config.checkout.cmsMode : false;
}

export const getCmsMode = createSelector(
    [
        getListingConfig,
    ],
    (
        config,
    ) => {
        return getCmsModeFromConfig(config);
    }
);

export const isCurrentRouterState = (state) => {
    return createSelector([getRouter], (router) => {
        if (router.last && typeof router.last.$to === 'function') {
            const $to = router.last.$to();
            if (typeof $to === 'object' && $to.name === state) {
                return true;
            }
        }
        return false;
    });
}

export const getFeeDisplayOption = createSelector(
    [
        getCheckoutConfig,
    ],
    (
        checkoutConfig,
    ) => {
        const options = [TICKET_FEE_SEPARATE, TICKET_PRICE_ONLY];
        if (checkoutConfig.feeDisplayOption && options.indexOf(+checkoutConfig.feeDisplayOption) !== -1) {
            return +checkoutConfig.feeDisplayOption;
        }
        return TICKET_FEE_SEPARATE; //default
    }
);

export function isDevMode() {
    return false;
}
